import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Users, Search, Upload, Send, CheckCircle, 
  Trash2, Mail, MoreVertical, Filter, Tag, X, Loader2,
  UserPlus, FileSpreadsheet, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import BulkEmailImport from '../components/BulkEmailImport';
import EmailCandidateModal from '../components/EmailCandidateModal';

const API = process.env.REACT_APP_BACKEND_URL;

const CandidatePoolPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedCandidates, setSelectedCandidates] = useState(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailCandidate, setEmailCandidate] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState('');
  const [inviting, setInviting] = useState(false);
  
  const token = localStorage.getItem('company_token');

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchCandidates();
    fetchChallenges();
  }, [token, navigate, searchTerm, statusFilter]);

  const fetchCandidates = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);
      params.append('limit', '100');

      const res = await axios.get(`${API}/api/company/candidates?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCandidates(res.data.candidates || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('company_token');
        navigate('/company/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchChallenges = async () => {
    try {
      const res = await axios.get(`${API}/api/company/enterprise/challenges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenges(res.data.challenges || []);
    } catch (err) {
      console.error('Failed to fetch challenges:', err);
    }
  };

  const handleBulkInvite = async () => {
    if (!selectedChallenge) {
      toast.error('Please select a challenge');
      return;
    }
    
    if (selectedCandidates.size === 0) {
      toast.error('Please select candidates to invite');
      return;
    }

    setInviting(true);
    try {
      const res = await axios.post(
        `${API}/api/company/candidates/bulk-invite`,
        {
          candidate_ids: Array.from(selectedCandidates),
          challenge_id: selectedChallenge
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );

      toast.success(`Invited ${res.data.invited_count} candidates to ${res.data.challenge_title}`);
      setShowInviteModal(false);
      setSelectedCandidates(new Set());
      setSelectedChallenge('');
      fetchCandidates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invitations');
    } finally {
      setInviting(false);
    }
  };

  const toggleSelectCandidate = (id) => {
    const newSet = new Set(selectedCandidates);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedCandidates(newSet);
  };

  const selectAll = () => {
    setSelectedCandidates(new Set(candidates.map(c => c.id)));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-700';
      case 'invited': return 'bg-purple-100 text-purple-700';
      case 'completed': return 'bg-green-100 text-green-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link 
                to="/company/dashboard" 
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </Link>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">Recruitment</p>
                <h1 className="text-lg font-semibold text-gray-900">Candidate Pool</h1>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm font-medium"
              >
                <Upload size={16} />
                Import
              </button>
              
              {selectedCandidates.size > 0 && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 text-sm font-medium"
                >
                  <Send size={16} />
                  Invite ({selectedCandidates.size})
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Total Candidates</p>
            <p className="text-2xl font-bold text-gray-900">{total}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">New</p>
            <p className="text-2xl font-bold text-blue-600">
              {candidates.filter(c => c.status === 'new').length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Invited</p>
            <p className="text-2xl font-bold text-purple-600">
              {candidates.filter(c => c.status === 'invited').length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Selected</p>
            <p className="text-2xl font-bold text-green-600">{selectedCandidates.size}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">All Status</option>
              <option value="new">New</option>
              <option value="invited">Invited</option>
              <option value="completed">Completed</option>
              <option value="rejected">Rejected</option>
            </select>
            
            <button
              onClick={selectAll}
              className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 text-sm"
            >
              Select All
            </button>
          </div>
        </div>

        {/* Candidates Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <Loader2 className="mx-auto animate-spin text-purple-600 mb-2" size={32} />
              <p className="text-gray-500">Loading candidates...</p>
            </div>
          ) : candidates.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="mx-auto text-gray-300 mb-3" size={48} />
              <p className="text-gray-500 mb-4">No candidates yet</p>
              <button
                onClick={() => setShowImportModal(true)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg inline-flex items-center gap-2"
              >
                <Upload size={16} />
                Import Candidates
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedCandidates.size === candidates.length && candidates.length > 0}
                      onChange={() => {
                        if (selectedCandidates.size === candidates.length) {
                          setSelectedCandidates(new Set());
                        } else {
                          selectAll();
                        }
                      }}
                      className="w-4 h-4 text-purple-600 rounded"
                    />
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Candidate</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Added</th>
                  <th className="w-12 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {candidates.map((candidate) => (
                  <tr 
                    key={candidate.id}
                    className={`hover:bg-gray-50 transition-colors ${
                      selectedCandidates.has(candidate.id) ? 'bg-purple-50' : ''
                    }`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedCandidates.has(candidate.id)}
                        onChange={() => toggleSelectCandidate(candidate.id)}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">{candidate.name}</p>
                        <p className="text-sm text-gray-500">{candidate.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(candidate.status)}`}>
                        {candidate.status?.charAt(0).toUpperCase() + candidate.status?.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500 capitalize">
                        {candidate.source?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-500">
                        {new Date(candidate.created_at).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button 
                          onClick={() => {
                            setEmailCandidate(candidate);
                            setShowEmailModal(true);
                          }}
                          className="p-1.5 hover:bg-purple-100 rounded text-gray-400 hover:text-purple-600 transition-colors"
                          title="Email candidate"
                        >
                          <Mail size={16} />
                        </button>
                        <button className="p-1 hover:bg-gray-100 rounded">
                          <MoreVertical size={16} className="text-gray-400" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-semibold text-gray-900">Import Candidates</h3>
              <button 
                onClick={() => setShowImportModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="p-0">
              <BulkEmailImport 
                onComplete={() => {
                  setShowImportModal(false);
                  fetchCandidates();
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Send Invitations
              </h3>
              
              <p className="text-sm text-gray-600 mb-4">
                Invite {selectedCandidates.size} candidate(s) to participate in a challenge.
              </p>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Challenge
                </label>
                <select
                  value={selectedChallenge}
                  onChange={(e) => setSelectedChallenge(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Choose a challenge...</option>
                  {challenges.filter(c => c.status === 'live').map(c => (
                    <option key={c.id} value={c.id}>{c.title}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkInvite}
                  disabled={inviting || !selectedChallenge}
                  className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {inviting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send Invites
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Candidate Modal */}
      <EmailCandidateModal
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setEmailCandidate(null);
        }}
        candidateEmail={emailCandidate?.email}
        candidateName={emailCandidate?.name}
        candidateId={emailCandidate?.id}
      />
    </div>
  );
};

export default CandidatePoolPage;
